import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  MapPin,
  Weight,
  DollarSign,
  TrendingUp,
  Truck,
  User,
  Hash,
  Package,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import React, { useState } from "react";
import { toast } from "sonner";
import { DateRange } from "./DateRange";
import { cn } from "@/lib/utils";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import LoadEventCard from "./LoadEventCard";
import { getCarriers } from "@/api/broker/getCarriers";
import { updateLoadInfo } from "@/api/broker/updateLoadInfo";
import { Load } from "@/api/loads/getLoads";

type Props = {
  load: Load;
  setIsOpen: (isOpen: boolean) => void;
  isOpen: boolean;
  badgeColor?: string;
};

export const AssignEditLoadModal = ({
  load,
  setIsOpen,
  isOpen,
  badgeColor,
}: Props) => {
  const { data: carriers, isLoading: isCarriersLoading } = useQuery({
    queryKey: ["broker", "carriers"],
    queryFn: getCarriers,
  });

  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    carrierId: load?.carrierId,
    driverName: load?.driverName,
    driverLicenseNumber: load?.driverLicenseNumber,
    truckVinLast6: load?.truckVinLast6,
    truckLicensePlate: load?.truckLicensePlate,
    trailerNumber: load?.trailerNumber,
    trailerLicensePlate: load?.trailerLicensePlate,
    carrierPay: load?.carrierPay ? load.carrierPay / 100 : null,
    receiverCost: load?.receiverCost ? load.receiverCost / 100 : null,
    bol: load.bol || "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetForm = () => {
    setFormData({
      carrierId: load?.carrierId,
      driverName: load?.driverName,
      driverLicenseNumber: load?.driverLicenseNumber,
      truckVinLast6: load?.truckVinLast6,
      truckLicensePlate: load?.truckLicensePlate,
      trailerNumber: load?.trailerNumber,
      trailerLicensePlate: load?.trailerLicensePlate,
      carrierPay: load?.carrierPay ? load.carrierPay / 100 : null,
      receiverCost: load?.receiverCost ? load.receiverCost / 100 : null,
      bol: load.bol || "",
    });
  };

  const selectedCarrier = carriers?.find((c) => c.id === formData.carrierId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.carrierId ||
      !formData.driverName ||
      !formData.driverLicenseNumber
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const isNewAssignment = load.status !== "Assigned";

      await updateLoadInfo({
        id: load.id,
        carrierId: formData.carrierId,
        driverName: formData.driverName,
        driverLicenseNumber: formData.driverLicenseNumber,
        truckVinLast6: formData.truckVinLast6,
        truckLicensePlate: formData.truckLicensePlate,
        trailerNumber: formData.trailerNumber,
        trailerLicensePlate: formData.trailerLicensePlate,
        receiverCostCents: (formData.receiverCost ?? 0) * 100,
        carrierPayCents: (formData.carrierPay ?? 0) * 100,
        bol: load.bol || "",
      });

      toast.success(
        isNewAssignment
          ? "Load assigned successfully!"
          : "Load updated successfully!"
      );
      handleClose();
      queryClient.invalidateQueries({
        queryKey: ["broker", "loads"],
      });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);

    resetForm();
  };

  const formatWeight = (weight: number | null) => {
    if (!weight) return "N/A";
    return `${(weight / 1000).toFixed(1)}k lbs`;
  };

  // const formatCurrency = (amount: number | null) => {
  //   if (!amount) return "N/A";

  //   return new Intl.NumberFormat("en-US", {
  //     style: "currency",
  //     currency: "USD",
  //     minimumFractionDigits: 0,
  //     maximumFractionDigits: 0,
  //   }).format(amount / 100);
  // };

  const isAssigned = load?.status === "Assigned";
  const events = load?.events || [];

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] rounded-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            {load.name} - {isAssigned ? "Edit Assignment" : "Assign Carrier"}
            <Badge variant="warning" className={badgeColor}>
              {load.status}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="grid sm:grid-cols-1 grid-cols-2 gap-6">
          {/* Load Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Load Information</h3>
            <Card>
              <CardContent className="p-4 space-y-4">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Route</p>
                    <p className="text-sm text-muted-foreground">
                      {load.sourceLocation} → {load.destinationLocation}
                    </p>
                  </div>
                </div>

                <DateRange load={load} />
                <div className="grid grid-cols-3 gap-4">
                  <div className="flex items-center gap-2">
                    <Weight className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">Weight</p>
                      <p className="text-sm text-muted-foreground">
                        {formatWeight(load?.weight)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">Pieces</p>
                      <p className="text-sm text-muted-foreground">
                        {load.pieces}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Hash className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">BOL Number</p>
                      <Input
                        type="string"
                        value={formData.bol ?? ""}
                        placeholder="Enter BOL number"
                        onChange={(value) =>
                          setFormData((prev) => ({
                            ...prev,
                            bol: value.target.value || "",
                          }))
                        }
                        className="text-sm text-muted-foreground"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-2 border-t">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">VS Cost</p>
                      <Input
                        type="string"
                        value={formData.receiverCost ?? ""}
                        placeholder="Enter cost"
                        onChange={(value) =>
                          setFormData((prev) => ({
                            ...prev,
                            receiverCost: parseFloat(value.target.value) || 0,
                          }))
                        }
                        className="text-sm text-muted-foreground"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">Carrier Cost</p>
                      <Input
                        type="string"
                        value={formData.carrierPay ?? ""}
                        placeholder="Enter cost"
                        onChange={(value) =>
                          setFormData((prev) => ({
                            ...prev,
                            carrierPay: parseFloat(value.target.value) || 0,
                          }))
                        }
                        className="text-sm text-muted-foreground"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <LoadEventCard events={events} />
          </div>

          {/* Assignment Form */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">
              {isAssigned ? "Assignment Details" : "Assign Carrier"}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              <Card>
                <CardContent className="p-4 space-y-4">
                  {/* Carrier Selection */}
                  <div>
                    <Label htmlFor="carrier">Carrier *</Label>
                    <Select
                      value={formData.carrierId ?? ""}
                      disabled={isCarriersLoading}
                      key={carriers?.length}
                      onValueChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          carrierId: value,
                        }))
                      }
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a carrier" />
                      </SelectTrigger>
                      <SelectContent>
                        {carriers?.map((carrier) => (
                          <SelectItem key={carrier.id} value={carrier.id}>
                            <div className="flex items-center gap-2">
                              <span>{carrier.companyName}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Selected Carrier Info */}

                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-3">
                      <Avatar className="w-8 h-8">
                        <AvatarImage src="/placeholder.svg" />
                        {selectedCarrier && (
                          <AvatarFallback>
                            {selectedCarrier.companyName
                              ?.split(" ")
                              .slice(0, 3)
                              .map((n) => n[0])
                              .join("")}
                          </AvatarFallback>
                        )}
                      </Avatar>

                      <div>
                        <p
                          className={cn(
                            "font-medium text-blue-900",
                            selectedCarrier === undefined
                              ? "text-gray-400 font-bold"
                              : ""
                          )}
                        >
                          {selectedCarrier?.companyName ?? "No Carrier"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Driver Information */}
                  <div className="space-y-4 pt-4 border-t">
                    <div className="flex items-center gap-2 mb-2">
                      <User className="w-4 h-4 text-muted-foreground" />
                      <h4 className="font-medium">Driver Information</h4>
                    </div>

                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <Label htmlFor="driverName">Driver Name *</Label>
                        <Input
                          id="driverName"
                          value={formData.driverName ?? ""}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              driverName: e.target.value,
                            }))
                          }
                          placeholder="Enter driver's full name"
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="driverLicenseNumber">
                          Driver License Number *
                        </Label>
                        <Input
                          id="driverLicenseNumber"
                          value={formData.driverLicenseNumber ?? ""}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              driverLicenseNumber: e.target.value,
                            }))
                          }
                          placeholder="Enter driver's license number"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Truck Information */}
                  <div className="space-y-4 pt-4 border-t">
                    <div className="flex items-center gap-2 mb-2">
                      <Truck className="w-4 h-4 text-muted-foreground" />
                      <h4 className="font-medium">Truck Information</h4>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="truckVinLast6">
                          Truck VIN (Last 6)
                        </Label>
                        <Input
                          id="truckVinLast6"
                          value={formData.truckVinLast6 ?? ""}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              truckVinLast6: e.target.value,
                            }))
                          }
                          placeholder="Last 6 digits of VIN"
                          maxLength={6}
                        />
                      </div>

                      <div>
                        <Label htmlFor="truckLicensePlate">
                          Truck License Plate
                        </Label>
                        <Input
                          id="truckLicensePlate"
                          value={formData.truckLicensePlate ?? ""}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              truckLicensePlate: e.target.value,
                            }))
                          }
                          placeholder="Truck license plate"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Trailer Information */}
                  <div className="space-y-4 pt-4 border-t">
                    <div className="flex items-center gap-2 mb-2">
                      <Hash className="w-4 h-4 text-muted-foreground" />
                      <h4 className="font-medium">Trailer Information</h4>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="trailerNumber">Trailer Number</Label>
                        <Input
                          id="trailerNumber"
                          value={formData.trailerNumber ?? ""}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              trailerNumber: e.target.value,
                            }))
                          }
                          placeholder="Enter trailer number"
                        />
                      </div>

                      <div>
                        <Label htmlFor="trailerLicensePlate">
                          Trailer License Plate
                        </Label>
                        <Input
                          id="trailerLicensePlate"
                          value={formData.trailerLicensePlate ?? ""}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              trailerLicensePlate: e.target.value,
                            }))
                          }
                          placeholder="Trailer license plate"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Submit Buttons */}
                  <div className="flex gap-2 pt-4 border-t">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleClose}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={
                        isSubmitting ||
                        !formData.carrierId ||
                        !formData.driverName ||
                        !formData.driverLicenseNumber
                      }
                      className="flex-1"
                    >
                      {isSubmitting
                        ? "Saving..."
                        : isAssigned
                        ? "Update Assignment"
                        : "Assign Load"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
